import { api } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import {
  AlertCircle,
  CheckCircle2,
  CloudUpload,
  ExternalLink,
  FileText,
  RefreshCw,
} from 'lucide-react';
import { useRef, useState } from 'react';

interface ProofUploaderProps {
  purchaseId: string;
  onUploadSuccess: (proofUrl: string) => void;
  existingProofUrl?: string;
  existingProofFilename?: string;
  existingBankReference?: string;
}

export default function ProofUploader({
  purchaseId,
  onUploadSuccess,
  existingProofUrl,
  existingProofFilename,
  existingBankReference,
}: ProofUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(existingProofUrl || null);
  const [pdfFileName, setPdfFileName] = useState<string>(existingProofFilename || '');
  const [bankReference, setBankReference] = useState(existingBankReference || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const downscaleImageToJpeg = async (
    file: File,
    maxDimension: number,
    quality: number
  ): Promise<File> => {
    try {
      if (!file.type.startsWith('image/')) return file;

      const bitmap = await createImageBitmap(file);
      const { width, height } = bitmap;
      const scale = Math.min(1, maxDimension / Math.max(width, height));
      const targetWidth = Math.max(1, Math.round(width * scale));
      const targetHeight = Math.max(1, Math.round(height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) return file;
      ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
      bitmap.close();

      const blob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => {
            if (!b) reject(new Error('No se pudo generar la imagen optimizada'));
            else resolve(b);
          },
          'image/jpeg',
          quality
        );
      });

      const optimizedName = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
      return new File([blob], optimizedName, { type: 'image/jpeg' });
    } catch {
      return file;
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setError('Solo se permiten imágenes (JPG, PNG) o archivos PDF');
      return;
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('El archivo no debe superar los 5MB');
      return;
    }

    setError('');
    setIsUploading(true);

    try {
      const fileToUpload = file.type.startsWith('image/')
        ? await downscaleImageToJpeg(file, 1600, 0.82)
        : file;

      if (fileToUpload.size > 5 * 1024 * 1024) {
        setError('El archivo optimizado no debe superar los 5MB');
        return;
      }

      // Si existe un comprobante previo, eliminarlo del storage
      if (previewUrl) {
        try {
          // Extraer el path del archivo de la URL
          const urlObj = new URL(previewUrl);
          const pathMatch = urlObj.pathname.match(/\/event-assets\/(.+)$/);
          if (pathMatch && pathMatch[1]) {
            const oldFilePath = pathMatch[1];
            console.log('Eliminando comprobante anterior:', oldFilePath);
            await supabase.storage.from('event-assets').remove([oldFilePath]);
          }
        } catch (deleteErr) {
          console.warn('No se pudo eliminar el comprobante anterior:', deleteErr);
          // Continuar con la subida del nuevo archivo aunque falle la eliminación
        }
      }

      // 1. Subir archivo a Supabase Storage
      const fileExt = fileToUpload.name.split('.').pop();
      const fileName = `${purchaseId}-${Date.now()}.${fileExt}`;
      const filePath = `payment-proofs/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('event-assets')
        .upload(filePath, fileToUpload, {
          cacheControl: '31536000',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // 2. Obtener URL pública del archivo
      const {
        data: { publicUrl },
      } = supabase.storage.from('event-assets').getPublicUrl(uploadData.path);

      // 3. Vista previa (solo para imágenes)
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(fileToUpload);
        setPdfFileName('');
      } else {
        // Para PDFs, guardar la URL pública y el nombre del archivo
        setPreviewUrl(publicUrl);
        setPdfFileName(file.name);
      }

      // 4. Enviar URL y nombre del archivo al backend
      await api.post(`/purchases/${purchaseId}/upload-proof`, {
        paymentProofUrl: publicUrl,
        paymentProofFilename: file.name,
        bankReference: bankReference || undefined,
      });

      onUploadSuccess(publicUrl);
    } catch (err) {
      console.error('Error uploading proof:', err);
      setError(err instanceof Error ? err.message : 'Error al subir el comprobante');
      setPreviewUrl(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Extraer nombre del archivo de la URL
  const extractFileName = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      const fileName = pathParts[pathParts.length - 1];
      return decodeURIComponent(fileName);
    } catch {
      return 'comprobante.pdf';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
        <CloudUpload className="h-6 w-6 text-green-600 mr-2" />
        Subir Comprobante de Pago
      </h3>

      <p className="text-gray-600 mb-4">
        Una vez que hayas realizado la transferencia, sube tu comprobante para que podamos verificar
        tu pago.
      </p>

      {/* Referencia Bancaria (opcional) */}
      <div className="mb-4">
        <label htmlFor="bankReference" className="block text-sm font-medium text-gray-700 mb-2">
          Referencia Bancaria (opcional)
        </label>
        <input
          type="text"
          id="bankReference"
          value={bankReference}
          onChange={(e) => setBankReference(e.target.value)}
          placeholder="Ej: 123456789"
          className="input-field"
          disabled={isUploading}
        />
        <p className="mt-1 text-sm text-gray-500">
          Si tu banco generó un número de referencia, ingrésalo aquí
        </p>
      </div>

      {/* Vista Previa */}
      {previewUrl && (
        <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle2 className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <p className="font-medium text-green-900">Comprobante subido exitosamente</p>
                <p className="text-sm text-green-700">
                  Tu comprobante será revisado en las próximas 24 horas
                </p>
              </div>
            </div>
            <button
              onClick={handleButtonClick}
              disabled={isUploading}
              className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full shadow-sm hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              title="Cambiar comprobante"
            >
              <RefreshCw className="h-4 w-4 text-blue-700" />
              Cambiar
            </button>
          </div>

          {/* Mostrar imagen o PDF */}
          {previewUrl.startsWith('data:image') ? (
            <div className="mt-3">
              <img
                src={previewUrl}
                alt="Comprobante de pago"
                className="max-w-full h-auto rounded-lg shadow-sm max-h-64 object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
          ) : previewUrl.startsWith('http') && (previewUrl.endsWith('.pdf') || pdfFileName) ? (
            <button
              onClick={() => window.open(previewUrl, '_blank')}
              className="mt-3 w-full p-4 bg-white rounded-lg border border-gray-200 flex items-center hover:bg-gray-50 transition-colors cursor-pointer"
              title="Clic para descargar/visualizar el PDF"
            >
              <FileText className="h-12 w-12 text-red-600 mr-3 flex-shrink-0" />
              <div className="flex-1 text-left">
                <p className="font-medium text-gray-900">Archivo PDF</p>
                <p className="text-sm text-gray-600 break-all">
                  {pdfFileName || extractFileName(previewUrl)}
                </p>
                <p className="text-xs text-blue-600 mt-1">Clic para visualizar/descargar</p>
              </div>
              <ExternalLink className="h-6 w-6 text-gray-400 flex-shrink-0" />
            </button>
          ) : (
            <div className="mt-3">
              <img
                src={previewUrl}
                alt="Comprobante de pago"
                className="max-w-full h-auto rounded-lg shadow-sm max-h-64 object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
          )}
        </div>
      )}

      {/* Botón de Upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/jpg,application/pdf"
        onChange={handleFileChange}
        className="hidden"
        disabled={isUploading}
      />

      {!previewUrl && (
        <>
          <button
            onClick={handleButtonClick}
            disabled={isUploading}
            className="w-full btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Subiendo comprobante...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <CloudUpload className="h-6 w-6 mr-2" />
                Seleccionar Comprobante
              </div>
            )}
          </button>

          <p className="mt-2 text-sm text-gray-500 text-center">
            Formatos permitidos: JPG, PNG, PDF (máx. 5MB)
          </p>
        </>
      )}

      {/* Error */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
}
